import { useCallback, useState, useEffect, memo } from 'react'
import { useTranslation } from 'react-i18next'
import jwt_decode from 'jwt-decode'
import { useDispatch } from 'react-redux'

import { useAppSelector } from 'redux/Hook'
import CustomButton from 'components/Shared/Button'
import ContentDetails from 'components/Reporting/Overview/ShareDashboard/ContentDetails'
import ShareDetails from 'components/Reporting/Overview/ShareDashboard'
import { useAlert } from 'components/Shared/Alert'
import { trackActions, capitalize } from 'containers/Helper'
import { setReportingDetails } from 'redux/slices/ShareReportingDetails'
import {
  setSharableFilterLink,
  setSharedModalFlow,
} from 'redux/slices/FilterBuilder'
import { handleApiCalls } from 'containers/Reporting/Overview/GraphicalDetails/ShareDashboard/Constants'
import { trackIntercomEvent } from 'lib/IntercomTracker/Intercom'
import sharedDetailsApi from 'api/ApiActions/Reporting/Overview/ShareDashboard'

const ShareDashboard = (props: any) => {
  const { isMobileDrawer, isTestPropsEnabled } = props

  const intialState = {
    link: '',
    password: '',
    id: '',
    linkSwitch: false,
    passwordSwitch: false,
    showPassword: false, // used for icon updates for show/hide password
  }
  const dispatch = useDispatch()

  const {
    features: {
      features: { overview_dashboard_sharing: { enabled = false } = {} } = {},
    } = {},
    filterBuilder: { isSharedModalFlow = false } = {},
    shareReportingDetails: {
      details: {
        link: sharedLink = '',
        password: sharedPassword = '',
        id: sharedId = '',
        shedUrlId = '',
      } = {},
    } = {},
    shareReportingDetails,
  }: any = useAppSelector((state) => state)

  const { t } = useTranslation()
  const { showAlert } = useAlert()

  const [modalVisibility, setModalVisibility] = useState(false)
  const [values, setValues] = useState<any>(intialState)
  const [loader, setLoader] = useState<any>({
    linkLoader: false,
    passwordLoader: false,
  })

  const { link, password, linkSwitch, passwordSwitch } = values

  const handleCopyClick = useCallback(
    async ({ placeholder }: { placeholder: string }) => {
      const copied =
        (await placeholder) === 'link details' ? 'both' : placeholder
      trackActions('Copy button Clicked', {
        featureName: 'Overview Dashboard',
        sectionName: 'Sharing Popup',
        copied,
      })
      //copies data based on the placehilder
      const decodeValue = (value: string) => jwt_decode(value)
      const valueToCopy = await (placeholder === 'password'
        ? decodeValue(values[placeholder])
        : values[placeholder])
      const copiedValue =
        valueToCopy ||
        `\n ${
          linkSwitch ? `${t('reporting.shareDetails.link')}: ${link}` : ''
        }  \n ${
          passwordSwitch
            ? `${t('reporting.shareDetails.password')}: ${decodeValue(
                password,
              )}`
            : ''
        }`

      if ('clipboard' in navigator) {
        await navigator.clipboard.writeText(copiedValue)
      } else {
        await document?.execCommand('copy', true, copiedValue)
      }
      await showAlert({
        type: 'success',
        title: `${capitalize(placeholder)} ${t(
          'reporting.shareDetails.copiedToClipBoard',
        )}`,
      })
      placeholder !== 'password' &&
        sharedDetailsApi.logDetails(sharedId || shedUrlId)
    },
    [values],
  )

  const alertUpdatesOnSwitches = (switchName: string) => {
    if (switchName === 'linkSwitch' && values[switchName]) {
      return showAlert({
        type: 'success',
        title: t('reporting.shareDetails.linkdisabled'),
      })
    }
    return (
      !values[switchName] &&
      showAlert({
        type: 'success',
        title:
          switchName === 'passwordSwitch'
            ? t('reporting.shareDetails.pwdProtected')
            : t('reporting.shareDetails.newLinkGenerated'),
      })
    )
  }

  const apiFailure = () => {
    showAlert({
      type: 'error',
      title: t('reporting.shareDetails.somethingWrong'),
    })
  }

  const updateLoader = (switchLoader: string) => {
    setLoader((prevState: any) => ({
      ...prevState,
      [switchLoader]: !prevState[switchLoader],
    }))
  }

  const linkSwitchUpdate = useCallback(
    async (targetVal: boolean) => {
      let response: any
      const idDetails = sharedId || shedUrlId
      setValues((prevState: any) => ({
        ...prevState,
        linkSwitch: true,
      }))
      if (targetVal) {
        response = handleApiCalls({ action: 'updateLink', sharedId: idDetails })
        const { link = '', id: responseId } = await response
        setValues((prevState: any) => ({
          ...prevState,
          linkSwitch: !!link,
          link,
        }))
        dispatch(
          setReportingDetails({
            link,
            id: responseId,
            shedUrlId,
          }),
        )
      } else {
        await handleApiCalls({
          action: 'deleteLink',
          sharedId,
        })

        setValues((prevState: any) => ({
          ...prevState,
          ...intialState,
        }))
        dispatch(setReportingDetails({ link: '', password: '', id: shedUrlId })) //cant make the id null
      }

      return response
    },
    [shareReportingDetails],
  )

  const passwordSwitchUpdate = useCallback(
    async (tagetVal: boolean) => {
      let response: any
      setValues((prevState: any) => ({
        ...prevState,
        passwordSwitch: true,
      }))
      if (tagetVal) {
        response = await handleApiCalls({
          action: 'getPassword',
          sharedId,
        })

        const { password = '' } = await response

        setValues((prevState: any) => ({
          ...prevState,
          passwordSwitch: !!password,
          password,
        }))
        dispatch(
          setReportingDetails({
            password,
          }),
        )
      } else {
        await handleApiCalls({ action: 'deletePassword', sharedId })
        dispatch(
          setReportingDetails({
            password: '',
          }),
        )
        setValues((prevState: any) => ({
          ...prevState,
          passwordSwitch: false,
          password: '',
          showPassword: false,
        }))
      }

      return response
    },
    [shareReportingDetails],
  )

  const updateData = async (switchName: string, targetVal: boolean) =>
    switchName === 'linkSwitch'
      ? linkSwitchUpdate(targetVal)
      : passwordSwitchUpdate(targetVal)

  /**
   * update data and call apis based on switch selection
   */
  const toggleValue = (value: boolean) => {
    return value ? 'On' : 'Off'
  }

  const onCloseModal = () => {
    trackActions('Sharing popup closed', {
      featureName: 'Overview Dashboard',
      sectionName: 'Sharing Popup',
      shareLinkValue: toggleValue(linkSwitch),
      sharePasswordValue: toggleValue(passwordSwitch),
    })
    dispatch(setSharedModalFlow(false))
    dispatch(setSharableFilterLink(false))
    setModalVisibility(false)
  }

  const handleSwitchUpdate = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const switchName = `${e.target.name}Switch`
    const switchLoader = `${e.target.name}Loader`

    updateLoader(switchLoader) // set loader true

    try {
      const response = await updateData(switchName, e.target.checked) // call API based on selection/deselection
      const checkIfDisabled = e.target.checked && !response // for disabled state

      updateLoader(switchLoader) // set loader false

      if (!response?.link || !response?.password || checkIfDisabled) {
        alertUpdatesOnSwitches(switchName)
      } else {
        apiFailure()
      }
    } catch (error) {
      console.error('Error updating switch:', error)
      updateLoader(switchLoader) // set loader false in case of error
      apiFailure()
    }
  }

  /**
   * used to show hide the password visibilty and update the icon for the same
   */
  const handleClickShowPassword = () => {
    setValues((prevState: any) => ({
      ...prevState,
      showPassword: !prevState.showPassword,
    }))
  }
  const contentDetailsProps = {
    handleCopyClick,
    handleSwitchUpdate,
    handleClickShowPassword,
    values,
    loader,
  }
  const customDialogProps = {
    title: t('reporting.shareDetails.title'),
    showCloseBtn: true,
    enableActionButtons: true,
    content: <ContentDetails {...contentDetailsProps} />,
    onCloseModal,
    isModalOpen: modalVisibility,
    ...(values.linkSwitch && {
      primaryBtn: (
        <CustomButton
          title={t('reporting.shareDetails.copyLinkDetails')}
          btnVariant="contained"
          onClick={() => {
            handleCopyClick({ placeholder: 'link details' })
          }}
        />
      ),
    }),
    ...(isTestPropsEnabled && {
      testProps: {
        disablePortal: true,
        hideBackdrop: true,
        TransitionComponent: ({ children }: any) => children,
      },
    }),
  }

  useEffect(() => {
    setValues({
      link: sharedLink,
      linkSwitch: !!sharedLink,
      password: sharedPassword,
      passwordSwitch: !!sharedPassword,
    })
  }, [sharedLink])

  const setModalVisibilityProp = (val: boolean) => {
    setModalVisibility(val)
    trackIntercomEvent('Share overview dashboard clicked')
    trackActions('Share Button Clicked', {
      featureName: 'Overview Dashboard',
    })
  }
  const shareDetailsProps = {
    isMobileDrawer,
    customDialogProps,
    setModalVisibilityProp,
  }

  useEffect(() => {
    isSharedModalFlow && setModalVisibility(true)
  }, [isSharedModalFlow])

  return enabled ? <ShareDetails {...shareDetailsProps} /> : null
}

export default memo(ShareDashboard)
